import { useCallback, useContext } from "react";
import {
  BoardMachinesStates,
  shipmentLoaded,
  updateShipmentsState,
  boardNotFound,
  pageFailure,
  Shipment,
  ShipmentState,
  ShipmentsResponse,
  shipmentsLoaded,
} from "../../../../../reducers/uploadedBoard";
import { useAppDispatch } from "../../../store";
import { BoardIdContext, BoardShipmentIds } from "./reviewBoardFlowLoader";
import settings from "../../../../../constants/settings";
import useFetchAndTrace from "../../../fetchWithTrace";

export default function useRefreshShipments() {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const dispatch = useAppDispatch();
  const fetchAndTrace = useFetchAndTrace();
  const handleFetchShipment = useCallback(
    async (response: Response) => {
      if (response.ok) {
        const shipment = (await response.json()) as Shipment;
        dispatch(shipmentLoaded({ boardId, shipment }));
      } else if (response.status == 404) {
        dispatch(boardNotFound({ boardId }));
      } else {
        throw Error("Failed to fetch part selection checks");
      }
    },
    [boardId, dispatch],
  );

  const handleFetchShipments = useCallback(
    async (response: Response) => {
      if (response.ok) {
        const { shipments } = (await response.json()) as ShipmentsResponse;
        dispatch(shipmentsLoaded({ boardId, shipments }));
      } else if (response.status == 404) {
        dispatch(boardNotFound({ boardId }));
      } else {
        throw Error("Failed to fetch part selection checks");
      }
    },
    [boardId, dispatch],
  );

  const handleFetchError = useCallback(() => {
    if (boardId) {
      dispatch(pageFailure({ boardId }));
    }
  }, [dispatch, boardId]);

  const refreshShipment = useCallback(
    (shipmentId: string) => {
      void fetchAndTrace(
        `${settings.api_url}/boards/${boardId}/shipments/${shipmentId}`,
        handleFetchShipment,
        handleFetchError,
      );
    },
    [boardId, fetchAndTrace, handleFetchShipment, handleFetchError],
  );

  const refreshShipments = useCallback(() => {
    void fetchAndTrace(
      `${settings.api_url}/boards/${boardId}/shipments`,
      handleFetchShipments,
      handleFetchError,
    );
  }, [boardId, fetchAndTrace, handleFetchShipments, handleFetchError]);

  const handleShipmentsStateUpdates = useCallback(
    (nextStatesData: BoardMachinesStates, states: BoardMachinesStates) => {
      Object.entries(nextStatesData.shipment_states).forEach(
        ([shipmentId, nextStateContext]) => {
          dispatch(
            updateShipmentsState({
              boardId,
              shipmentId: shipmentId,
              shipmentsState: nextStateContext.state,
            }),
          );
          const previousStateContext = states.shipment_states[shipmentId];
          const becameProcessed =
            previousStateContext?.state !== ShipmentState.PROCESSED &&
            nextStateContext.state == ShipmentState.PROCESSED;
          // Some transitions save the shipment and leave its state where it
          // was, so watch the save timestamp too
          const savedWhileProcessed =
            nextStateContext.state == ShipmentState.PROCESSED &&
            previousStateContext?.datetime_updated !==
              nextStateContext.datetime_updated;
          if (becameProcessed || savedWhileProcessed) {
            refreshShipment(shipmentId);
          }
        },
      );
    },
    [boardId, dispatch, refreshShipment],
  );

  return {
    refreshShipments,
    handleShipmentsStateUpdates,
  };
}
